<script setup>
  import {ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentSearchWrapper from "@components/Autocomplete/SearchWrapper.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import RecordComponentMinCSearch from "@pages/Records/Components/MinCSearch.vue";
  import MedicationComponentIndicationCheckbox from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationCheckbox.vue";
  import {updateCompleteForm} from "@pages/Records/Components/Sections/MedicationBar/Utils/updateCompleteForm.js";
  import {MedButton, MedCheckbox, MedFormField, MedInfoButton, MedLabel} from "@pharma4u/medicheck-components";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";
  import {cloneDeep, debounce} from "es-toolkit";
  import {each, set} from "es-toolkit/compat";

  defineOptions({
    name: "MedicationComponentIndicationDialog",
  });

  const props = defineProps({
    isDetailDialog: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["close", "formChange"]);

  const page = usePage();

  const refIndicationMenu = ref(null);
  const refSearch = ref(null);

  const abDataKeyFam = ref(null);
  const customIndicationsList = ref([]);
  const hashId = ref("");
  const indicationList = ref([]);
  const indicationUnknown = ref(false);
  const isLoading = ref(true);
  const newIndicationList = ref(null);
  const unloadableIndications = ref([]);
  const medicationForm = ref({});

  watch(
    () => medicationForm.value.indications,
    (newValue) => {
      if (newValue && newValue.length > 0 && indicationUnknown.value) {
        handleFormChange({key: "indication_unknown", newValue: 0});
      }

      newIndicationList.value = newValue;
      reloadIndicationLists();
    },
    {deep: true},
  );

  watch(
    () => medicationForm.value.indication_unknown,
    (newValue) => {
      indicationUnknown.value = !!newValue;
    },
  );

  watch(
    () => indicationUnknown.value,
    (newValue) => {
      if (newValue) {
        if (props.isDetailDialog) {
          emit("formChange", {key: "indications", newValue: []});
          emit("formChange", {key: "indication_unknown", newValue: 1});
        } else {
          handleFormChange({key: "indications", newValue: []});
          handleFormChange({key: "indication_unknown", newValue: 1});
        }
      } else {
        if (props.isDetailDialog) {
          emit("formChange", {key: "indication_unknown", newValue: 0});
        } else {
          handleFormChange({key: "indication_unknown", newValue: 0});
        }
      }
    },
  );

  const open = ({medication, medicationHashId, medicationKeyFam}) => {
    medicationForm.value = medication;
    indicationUnknown.value = !!medicationForm.value.indication_unknown;
    newIndicationList.value = medicationForm.value.indications;

    abDataKeyFam.value = medicationKeyFam;
    hashId.value = medicationHashId;

    refIndicationMenu.value.open();

    isLoading.value = true;

    unloadableIndications.value = [];

    loadIndicationsFromCore();
  };

  const close = () => {
    refIndicationMenu.value.close();
    medicationForm.value = {};
  };

  const addIndication = (newIndication) => {
    const formattedIndication = {
      id: null,
      name: newIndication.Name,
      abdata_key_miv: newIndication.Key_MIV,
      name_icdc: newIndication.icdcs?.[0]?.Name || null,
      abdata_key_icdc: newIndication.icdcs?.[0]?.Key_ICDC || null,
      gender: newIndication.Geschlecht,
      counter: newIndication.Zaehler,
      indication_leads_to_disease: newIndication.indication_leads_to_disease,
    };

    const checkDouble = newIndicationList.value.some((indication) => indication.name === formattedIndication.name);

    if (!checkDouble) {
      newIndicationList.value.push(formattedIndication);

      if (props.isDetailDialog) {
        emit("formChange", {key: "indications", newValue: newIndicationList.value});
      } else {
        handleFormChange({key: "indications", newValue: newIndicationList.value});
      }
    }

    reloadIndicationLists();
  };

  const reloadIndicationLists = () => {
    customIndicationsList.value = [];
    indicationList.value = [];

    loadIndicationsFromCore();
  };

  const loadIndicationsFromCore = () => {
    isLoading.value = true;

    if (abDataKeyFam.value) {
      coreRouter.loadMinDataViaKeyFam(abDataKeyFam.value, {
        onSuccess: (response) => {
          indicationList.value = [];

          each(response.data, (indication) => {
            indicationList.value.push({
              id: null,
              name: indication.Name,
              abdata_key_miv: indication.Key_MIV,
              name_icdc: indication.name_icdc || null,
              abdata_key_icdc: indication.abdata_key_icdc || null,
              gender: indication.Geschlecht,
              counter: indication.Zaehler,
              indication_leads_to_disease: indication.indication_leads_to_disease,
            });
          });
        },
        onFinish: () => {
          isLoading.value = false;
          filterLists();
        },
      });
    } else {
      isLoading.value = false;
      filterLists();
    }
  };

  const filterLists = () => {
    if (newIndicationList.value === null || newIndicationList.value === undefined) {
      return;
    }

    newIndicationList.value.map((formIndication) => {
      if (formIndication.abdata_key_miv) {
        coreRouter.loadIndications(`?filter=indications&q[key_miv]=${formIndication.abdata_key_miv}`, {
          onSuccess: (response) => {
            if (response.data.length > 0) {
              if (!formIndication.gender) {
                const mainIndication = response.data.find((coreIndication) => coreIndication.Vorzugsbezeichnung === 1);

                formIndication.gender = mainIndication.Geschlecht;
              }
            } else {
              unloadableIndications.value = addIfNotInList(unloadableIndications, formIndication);
            }
          },
        });
      }

      const matchWithIndicationList = indicationList.value.find((indication) => {
        if (indication.abdata_key_icdc !== null) {
          return indication.abdata_key_icdc === formIndication.abdata_key_icdc;
        }

        return indication.name === formIndication.name;
      });

      if (matchWithIndicationList) {
        Object.assign(matchWithIndicationList, formIndication);
      } else {
        customIndicationsList.value = addIfNotInList(customIndicationsList, formIndication);
      }
    });
  };

  const addIfNotInList = (list, newValue) => {
    const checkDouble = list.value.some((item) => item.name === newValue.name);

    if (!checkDouble) list.value.push(newValue);

    return list.value;
  };

  const resetMedicationIndication = () => {
    if (props.isDetailDialog) {
      emit("close");
      close();
    } else {
      const medication = cloneDeep(
        page.props.medications?.data.find((oldMedication) => oldMedication.id === medicationForm.value.id),
      );

      medicationForm.value.indications = medication.indications;
      medicationForm.value.indication_unknown = medication.indication_unknown;

      close();
    }
  };

  const saveIndications = () => {
    if (props.isDetailDialog) {
      close();
    } else {
      isLoading.value = true;
      putMedication();
    }
  };

  const handleFormChange = ({key, newValue}) => {
    if (props.isDetailDialog) {
      emit("formChange", {key: key, newValue: newValue});
    } else {
      if (key) {
        set(medicationForm.value, key, newValue);
      } else {
        updateCompleteForm(medicationForm.value, newValue);
      }
    }
  };

  const putMedication = debounce(() => {
    router.put(
      route("medications.update", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        medication: medicationForm.value.id,
      }),
      {...medicationForm.value},
      {
        preserveScroll: true,
        only: ["flash", "errors", "medications", "diseases", "symptoms"],
        onFinish: () => {
          isLoading.value = false;
          close();
        },
      },
    );
  });

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refIndicationMenu"
    dialog-width="medium"
    dialog-title="Grund / Indikationen"
    :is-loading="isLoading"
    class="max-h-144"
    :test-id="getTestId('indication')"
  >
    <template #info-icon>
      <MedInfoButton
        size="lg"
        side="bottom"
        aria-label="Indikationen Info"
        disable-portal
        :data-test="getTestId('indication-dialog-info-button')"
      >
        <p class="max-w-sm text-sm">
          Wählen Sie aus typischen, für dieses FAM zugelassenen („in-label"-) Indikationen oder wählen Sie
          „Weitere/andere ICD-10-Code"
        </p>
      </MedInfoButton>
    </template>

    <template #content>
      <ComponentAlert
        v-if="unloadableIndications && unloadableIndications.length > 0"
        type="warning"
        :test-id="getTestId('indication-dialog')"
      >
        <h4 class="font-bold">
          In diesem Bereich befinden sich Indikationen, die in der aktualisierten Datenbank ersetzt (oder
          geschlechtsspezifisch zugeordnet) wurden.
        </h4>
        <p class="mt-2">
          Bitte tauschen Sie folgende Indikationen durch geeignete Neue aus, damit sie im Check erkannt und
          berücksichtigt werden:
        </p>
        <ul class="mt-2 space-y-1">
          <li v-for="(item, index) in unloadableIndications" :key="index">
            <em>{{ item.name }}</em>
          </li>
        </ul>
      </ComponentAlert>

      <template v-if="indicationList.length === 0">
        <div class="pb-2 text-sm">Keine Indikation nach ABDATA hinterlegt.</div>
      </template>

      <template v-if="indicationList.length > 0">
        <MedicationComponentIndicationCheckbox
          :indications-options="indicationList"
          :new-indication-list="newIndicationList"
          :indications-unknown="indicationUnknown"
          :hash-id="hashId"
          :is-loading="isLoading"
          :test-id="getTestId('indication-dialog')"
          @form-change="handleFormChange"
        />
      </template>

      <div class="flex border-y py-3">
        <MedFormField orientation="horizontal">
          <MedCheckbox v-model="indicationUnknown" :data-test="getTestId('indication-dialog-unknown-checkbox')" />

          <MedLabel>Unbekannt</MedLabel>
        </MedFormField>
      </div>

      <div class="mt-4 mb-2 text-sm text-gray-500">Weitere / andere / ICD-10 Code:</div>

      <MedicationComponentIndicationCheckbox
        :indications-options="customIndicationsList"
        :new-indication-list="newIndicationList"
        :indications-unknown="indicationUnknown"
        :hash-id="hashId"
        :is-loading="isLoading"
        :test-id="getTestId('indication-dialog-custom')"
        @form-change="handleFormChange"
      />

      <ComponentSearchWrapper :test-id="getTestId('indication-dialog')" :set-focus="() => $refs.refSearch.focus()">
        <RecordComponentMinCSearch
          ref="refSearch"
          class="w-96"
          :reset-on-select="true"
          filter="indications"
          placeholder="Suchen"
          :test-id="getTestId('indication-dialog')"
          @input="addIndication"
        />
      </ComponentSearchWrapper>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="isLoading"
        :data-test="getTestId('indication-dialog-save-button')"
        @click="saveIndications"
      >
        Speichern
      </MedButton>

      <MedButton
        :disabled="isLoading"
        :data-test="getTestId('indication-dialog-cancel-button')"
        @click="resetMedicationIndication"
      >
        Abbrechen
      </MedButton>
    </template>
  </ComponentDialog>
</template>
